import React, { FunctionComponent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DATA_QA } from '../../../helpers/data-qa';
import { INewsForgeTranslationContent, ITextContent } from '../../../models/news-forge.models';
import '../../../styles/news-forge.styles.scss';
import {
	autoResizeTextArea,
	countWords,
	joinTextAsHtmlParagraphs,
	NewsForgeErrorState,
	stripHtmlAndJoin,
} from '../../../helpers/news-forge.helper';
import { updateNewsForgeContent } from '../../../../../../store/action-creators/news-forge-action-creator';
import { useDispatch } from 'react-redux';
import ErrorIconInfo from '../../../../../../assets/icons/general/error-icon-info.svg';
import ReGenerateTextButton from '../buttons/re-generate-text-button';
import { useReGenerateTranslation } from '../../../helpers/news-forge-requests.helper';
import CreateArticleButton from '../buttons/create-article-button';

type Properties = {
	newsForgeContent: INewsForgeTranslationContent;
	setJobId: (id: string | null) => void;
	setIsExtracting: (val: boolean) => void;
	setErrorState: (errorState: NewsForgeErrorState) => void;
	setIsRegenerating: (val: boolean) => void;
};

const NewsForgeTextContainer: FunctionComponent<Properties> = ({
	newsForgeContent,
	setJobId,
	setIsExtracting,
	setErrorState,
	setIsRegenerating,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { mutate: reGenerateTranslation } = useReGenerateTranslation(setJobId, setIsExtracting);
	const TITLE_MAX_LENGTH = 255;

	// ORIGINAL STATE
	const [originalTitle, setOriginalTitle] = useState<string>(
		stripHtmlAndJoin((newsForgeContent && newsForgeContent.original_content && newsForgeContent.original_content.title) || ''),
	);
	const [originalContent, setOriginalContent] = useState<string>(
		stripHtmlAndJoin((newsForgeContent && newsForgeContent.original_content && newsForgeContent.original_content.content) || ''),
	);

	// TRANSLATED STATE
	const [translatedTitle, setTranslatedTitle] = useState<string>(
		stripHtmlAndJoin((newsForgeContent && newsForgeContent.translated_content && newsForgeContent.translated_content.title) || ''),
	);
	const [translatedContent, setTranslatedContent] = useState<string>(
		stripHtmlAndJoin((newsForgeContent && newsForgeContent.translated_content && newsForgeContent.translated_content.content) || ''),
	);

	// COUNTERS
	const originalTitleCharCount = originalTitle.length;
	const originalContentCharCount = originalContent.length;
	const originalContentWordCount = useMemo(() => countWords(originalContent), [originalContent]);

	const translatedTitleCharCount = translatedTitle.length;
	const translatedContentCharCount = translatedContent.length;
	const translatedContentWordCount = useMemo(() => countWords(translatedContent), [translatedContent]);

	const originalTitleError = originalTitle.length > TITLE_MAX_LENGTH;
	const translatedTitleError = translatedTitle.length > TITLE_MAX_LENGTH;

	const initialOriginalTitleRef = useRef(
		stripHtmlAndJoin((newsForgeContent && newsForgeContent.original_content && newsForgeContent.original_content.title) || ''),
	);
	const initialOriginalContentRef = useRef(
		stripHtmlAndJoin((newsForgeContent && newsForgeContent.original_content && newsForgeContent.original_content.content) || ''),
	);

	const hasOriginalChanged = originalTitle !== initialOriginalTitleRef.current || originalContent !== initialOriginalContentRef.current;

	const updateTextContent = (side: 'original_content' | 'translated_content', field: keyof ITextContent, value: string) => {
		const updatedContent: INewsForgeTranslationContent = {
			...newsForgeContent,
			[side]: {
				...newsForgeContent[side],
				[field]: joinTextAsHtmlParagraphs(value),
			},
		};
		dispatch(updateNewsForgeContent(updatedContent));
	};

	return (
		<div className='news-forge-text-container' data-qa={DATA_QA.NEWS_FORGE_TEXT_CONTAINER}>
			<div className='news-forge-text-columns'>
				{/* ORIGINAL TEXT COLUMN */}
				<div className='news-forge-text-column'>
					<div className='news-forge-text-content-section-header-container'>
						<span className='news-forge-text-content-header-label'>{t('original_content')}</span>
					</div>
					<div className='news-forge-text-content-container' data-qa={DATA_QA.NEWS_FORGE_ORIGINAL_CONTENT_CONTAINER}>
						{/* Title */}
						<div className='news-forge-text-content-header-container'>
							<span className='news-forge-text-content-section-label'>{t('title')}</span>
							<textarea
								className='news-forge-text-content-title-label'
								value={originalTitle}
								onChange={(e) => {
									setOriginalTitle(e.target.value);
									updateTextContent('original_content', 'title', e.target.value);
								}}
								onInput={(e) => autoResizeTextArea(e.currentTarget)}
								ref={(el) => autoResizeTextArea(el)}
							/>
							<div
								className={`news-forge-text-content-words-and-char-counter-container ${
									originalTitleError || originalTitle.trim() === '' ? 'error' : ''
								}`}
							>
								{(originalTitleError || originalTitle.trim() === '') && (
									<span className='news-forge-text-error-message'>
										<img src={ErrorIconInfo} alt='error' className='news-forge-error-icon' />
										{originalTitle.trim() === '' ? t('title_mandatory') : t('news_forge_title_error')}
									</span>
								)}
								<span
									data-qa={DATA_QA.NEWS_FORGE_CHAR_AND_WORDS_COUNTER}
									className={`news-forge-text-content-words-and-char-counter-label ${originalTitleError ? 'error' : ''}`}
								>
									{(originalTitle.trim() !== '' || originalTitle) && `${t('characters')} ${originalTitleCharCount} / 255`}
								</span>
							</div>
						</div>

						{/* Body */}
						<div className='news-forge-text-content-body-container'>
							<div className='news-forge-text-content-body-text-container'>
								<span className='news-forge-text-content-section-label'>{t('body')}</span>
								<textarea
									className='news-forge-text-content-body-text'
									value={originalContent}
									onChange={(e) => {
										setOriginalContent(e.target.value);
										updateTextContent('original_content', 'content', e.target.value);
									}}
									onInput={(e) => autoResizeTextArea(e.currentTarget)}
									ref={(el) => autoResizeTextArea(el)}
								/>
							</div>
							<div className='news-forge-text-content-words-and-char-counter-container'>
								<span data-qa={DATA_QA.NEWS_FORGE_CHAR_AND_WORDS_COUNTER} className='news-forge-text-content-words-and-char-counter-label'>
									{`${t('characters')} ${originalContentCharCount} / ${t('words')} ${originalContentWordCount}`}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* TRANSLATED TEXT COLUMN */}
				<div className='news-forge-text-column'>
					<div className='news-forge-text-content-section-header-container'>
						<span className='news-forge-text-content-header-label'>{t('translated_content')}</span>
						<div className='text-buttons-container'>
							<ReGenerateTextButton
								text={t('re_generate_text')}
								disabled={!hasOriginalChanged || originalTitle.trim() === '' || !originalTitle}
								onClickAction={() => {
									if (
										newsForgeContent &&
										newsForgeContent.original_content &&
										newsForgeContent.original_content.title &&
										newsForgeContent.original_content.content
									) {
										setIsRegenerating(true);
										reGenerateTranslation({
											title: stripHtmlAndJoin(newsForgeContent.original_content.title),
											content: newsForgeContent.original_content.content,
										});
									}
								}}
							/>
							<CreateArticleButton
								text={t('article_create_title')}
								setErrorState={setErrorState}
								disabled={translatedTitle.trim() === '' || !translatedTitle}
							/>
						</div>
					</div>
					<div className='news-forge-text-content-container' data-qa={DATA_QA.NEWS_FORGE_TRANSLATED_CONTENT_CONTAINER}>
						{/* Title */}
						<div className='news-forge-text-content-header-container'>
							<span className='news-forge-text-content-section-label'>{t('title')}</span>
							<textarea
								className='news-forge-text-content-title-label'
								value={translatedTitle}
								onChange={(e) => {
									setTranslatedTitle(e.target.value);
									updateTextContent('translated_content', 'title', e.target.value);
								}}
								onInput={(e) => autoResizeTextArea(e.currentTarget)}
								ref={(el) => autoResizeTextArea(el)}
							/>
							<div
								className={`news-forge-text-content-words-and-char-counter-container ${
									translatedTitleError || translatedTitle.trim() === '' ? 'error' : ''
								}`}
							>
								{(translatedTitleError || translatedTitle.trim() === '') && (
									<span className='news-forge-text-error-message'>
										<img src={ErrorIconInfo} alt='error' className='news-forge-error-icon' />
										{translatedTitle.trim() === '' ? t('title_mandatory') : t('news_forge_title_error')}
									</span>
								)}
								<span
									data-qa={DATA_QA.NEWS_FORGE_CHAR_AND_WORDS_COUNTER}
									className={`news-forge-text-content-words-and-char-counter-label ${translatedTitleError ? 'error' : ''}`}
								>
									{(translatedTitle.trim() !== '' || translatedTitle) && `${t('characters')} ${translatedTitleCharCount} / 255`}
								</span>
							</div>
						</div>

						{/* Body */}
						<div className='news-forge-text-content-body-container'>
							<div className='news-forge-text-content-body-text-container'>
								<span className='news-forge-text-content-section-label'>{t('body')}</span>
								<textarea
									className='news-forge-text-content-body-text'
									value={translatedContent}
									onChange={(e) => {
										setTranslatedContent(e.target.value);
										updateTextContent('translated_content', 'content', e.target.value);
									}}
									onInput={(e) => autoResizeTextArea(e.currentTarget)}
									ref={(el) => autoResizeTextArea(el)}
								/>
							</div>
							<div className='news-forge-text-content-words-and-char-counter-container'>
								<span data-qa={DATA_QA.NEWS_FORGE_CHAR_AND_WORDS_COUNTER} className='news-forge-text-content-words-and-char-counter-label'>
									{`${t('characters')} ${translatedContentCharCount} / ${t('words')} ${translatedContentWordCount}`}
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default NewsForgeTextContainer;
